<template>
  <div
    v-if="specList.length"
    :key="`${specList.length}_model-characteristics`"
    data-copy="for-copied"
    class="model-characteristics"
  >
    <h2 class="title heading-m">{{ title }}</h2>

    <div class="groups" :class="{ 'groups--wrap': isTab }">
      <div
        v-for="({ show, group, specifications }, index) in specList"
        :key="index"
        :class="{ group, 'group--hide': more && !show && !isBot }"
      >
        <p class="title heading-xs">{{ group }}</p>

        <div class="specifications body-l-regular">
          <div v-if="isSpecificationsItem(index)" class="item">
            <div class="label-group">
              <div class="label">
                <div class="filler" />
                <span class="body-l-regular white">Бренд</span>
              </div>
            </div>

            <div class="value body-l-regular">
              <nuxt-link :to="brandSlug" class="p_link">
                {{ brand.name }}
              </nuxt-link>
            </div>
          </div>
          <div v-if="categoryName && index === 0" class="item">
            <div class="label-group">
              <div class="label">
                <div class="filler" />
                <span class="body-l-regular white">Категория</span>
              </div>
            </div>

            <div class="value body-l-regular">
              <nuxt-link :to="`/${category.slug}/`" class="p_link">
                {{ categoryName }}
              </nuxt-link>
            </div>
          </div>

          <div
            v-for="(item, groupIndex) in specifications"
            :key="groupIndex"
            :class="{ item, 'item--hide': more && !item.show && !isBot }"
          >
            <div class="label-group">
              <div class="label">
                <div class="filler" />
                <span class="body-l-regular white">
                  {{ withoutLastWord(item.title) }}
                  <span class="nowrap">
                    {{ lastWord(item.title) }}
                    <ui-hint
                      v-if="item.description"
                      :text="item.description"
                      placement="right-start"
                      class="hint"
                    />
                  </span>
                </span>
              </div>
            </div>
            <div class="value body-l-regular">
              {{ item.value }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="showMoreLink && !isBot">
      <div ref="moreRef" class="more-link">
        <ui-ui-link
          :font-weight="400"
          :icon-after="more ? 'down' : 'up'"
          :responsive-ico="true"
          @click="clickMore"
        >
          {{ more ? 'Все характеристики' : 'Свернуть' }}
        </ui-ui-link>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  ModelBrand,
  ModelCategory,
  ModelGroupSpecification,
} from '~/interfaces/api';
import { useGlobalStore } from '~/stores/global';

const globalStore = useGlobalStore();
const isBot = computed(() => globalStore.isBot);

const moreRef = ref<HTMLElement>();
const props = defineProps({
  modelId: Number,
  name: String,
  brand: {
    type: Object as () => Partial<ModelBrand>,
    default: () => {},
  },
  category: {
    type: Object as () => Partial<ModelCategory>,
    default: () => {},
  },
  isTab: {
    type: Boolean,
    required: false,
    default: false,
  },
  spec: {
    type: Array as () => ModelGroupSpecification[],
    default: () => [],
  },
});

defineEmits(['to-route']);
const brandSlug = computed(() => {
  if (props.brand.slug && props.category.slug) {
    const brandReplace = props.brand.slug?.replace('brands/', '');
    return `/${props.category.slug}/${brandReplace}/`;
  }
  return `/${props.brand.slug}/`;
});
const categoryName = computed(() => props.category?.name || '');
const specLimit = computed(() => {
  const limit = 12;
  if (props.brand?.name) {
    return limit - 1;
  } else {
    return limit;
  }
});

const title = computed(
  () => `Характеристики ${props.brand.name + ' ' + props.name}`
);

const clickMore = () => {
  more.value = !more.value;
  nextTick(() => {
    if (more.value && window && moreRef.value) {
      moreRef.value.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  });
};

const characteristics = computed(() => {
  let count = 0;
  if (!props?.spec || ('success' in props?.spec && !props?.spec?.success))
    return [];
  const list = props.spec.map((group) => ({
    ...group,
    show: count <= specLimit.value,
    specifications: group.specifications.map((spec) => {
      count++;
      return {
        ...spec,
        show: count <= specLimit.value,
      };
    }),
  }));

  return { list, count };
});

const specList = computed(() => characteristics.value?.list);
const showMoreLink = computed(() => {
  if (props.isTab) {
    return false;
  } else {
    return characteristics.value?.count > specLimit.value;
  }
});
const more = ref(showMoreLink.value);

const isSpecificationsItem = (index: number): boolean => {
  return index === 0 && !!props.brand?.name;
};
const lastWord = (str: String) => {
  if (!str) return false;
  return str.trim().split(' ').pop();
};
const withoutLastWord = (str: String) => {
  if (!str) return false;
  const arr = str.trim().split(' ');
  arr.pop();
  return arr.join(' ');
};
</script>
<style lang="scss" src="./ModelCharacteristicsScss.scss" scoped></style>
